import { isBefore, isValid } from 'date-fns';
import { isValidSSN } from 'platform/forms-system/src/js/utilities/validations';
import { replaceStrValues } from 'platform/shared/health-apps';
import { formatDateLong } from 'platform/utilities/date';
import content from '../locales/en/content.json';

const ERR_PART_D_EFFECT_DATE = content['validation--part-d-effective-date'];
const ERR_SSN_UNIQUE = content['validation--ssn-unique'];
const ERR_SSN_INVALID = content['validation--ssn-invalid'];

const normalizeSsn = val => String(val ?? '').replace(/\D/g, '');

const getCurrentItemIndex = () => {
  try {
    const pathname = window?.location?.pathname || '';
    const match = pathname.match(/\/(\d+)(?:\?|$)/);
    return match ? parseInt(match[1], 10) : null;
  } catch {
    return null;
  }
};

const getSsnMatches = (fullData, current) => {
  const currentIndex = getCurrentItemIndex();
  const sponsor = normalizeSsn(fullData?.sponsorSsn);
  const applicants = (fullData?.applicants ?? [])
    .map(a => normalizeSsn(a?.applicantSsn))
    .filter(Boolean);

  const sponsorMatch = sponsor === current;
  const applicantMatches = applicants.filter(
    (ssn, index) =>
      ssn === current && (currentIndex === null || index !== currentIndex),
  ).length;

  return { sponsorMatch, applicantMatches, currentIndex };
};

const validateUniqueSsn = ({
  errors,
  fieldData,
  fullData,
  isSponsor = false,
} = {}) => {
  const current = normalizeSsn(fieldData);
  if (!current) return;

  if (!isValidSSN(current)) {
    errors.addError(ERR_SSN_INVALID);
    return;
  }

  const { sponsorMatch, applicantMatches, currentIndex } = getSsnMatches(
    fullData,
    current,
  );

  if (!isSponsor && sponsorMatch) {
    errors.addError(ERR_SSN_UNIQUE);
    return;
  }

  const duplicateThreshold = !isSponsor && currentIndex === null ? 1 : 0;
  if (applicantMatches > duplicateThreshold) {
    errors.addError(ERR_SSN_UNIQUE);
  }
};

/**
 * Validates that the sponsor's SSN is valid and unique among all form participants.
 * Checks that the SSN is properly formatted and not duplicated by any applicants.
 *
 * @param {Object} errors - The validation errors object to add errors to
 * @param {string} fieldData - The sponsor's Social Security Number to validate
 * @param {Object} fullData - The complete form data for cross-reference checking
 * @param {string} fullData.sponsorSsn - The sponsor's SSN
 * @param {Array} fullData.applicants - Array of applicant objects with their SSNs
 */
export const validateSponsorSsn = (errors, fieldData, fullData) => {
  validateUniqueSsn({ errors, fieldData, fullData, isSponsor: true });
};

/**
 * Validates that an applicant's SSN is valid and unique among all form participants.
 * Checks that the SSN is properly formatted, not duplicated by other applicants,
 * and does not match the sponsor's SSN.
 *
 * @param {Object} errors - The validation errors object to add errors to
 * @param {string} fieldData - The applicant's Social Security Number to validate
 * @param {Object} fullData - The complete form data for cross-reference checking
 * @param {string} fullData.sponsorSsn - The sponsor's SSN (to check for conflicts)
 * @param {Array} fullData.applicants - Array of all applicant objects with their SSNs
 */
export const validateApplicantSsn = (errors, fieldData, fullData) => {
  validateUniqueSsn({ errors, fieldData, fullData });
};

/**
 * Validates that only one applicant has a spousal relationship to the sponsor.
 * Prevents multiple spouse/partner applicants by checking existing relationships,
 * excluding the current applicant being edited.
 *
 * @param {Object} errors - The validation errors object to add errors to
 * @param {string} fieldData - The relationship value ('spouse', 'child', etc.)
 * @param {Object} formData - The complete form data containing all applicants
 */
export const validateSpousalRelationship = (errors, fieldData, formData) => {
  if (fieldData !== 'spouse') return;

  const currentIndex = getCurrentItemIndex();
  const hasCurrentIndex = Number.isInteger(currentIndex);
  const duplicateThreshold = hasCurrentIndex ? 0 : 1;

  const spouseCount = (formData?.applicants ?? []).filter(
    (a, i) =>
      a?.applicantRelationshipToSponsor === 'spouse' &&
      (!hasCurrentIndex || i !== currentIndex),
  ).length;

  if (spouseCount > duplicateThreshold) {
    errors.addError(content['validation--spousal-relationship']);
  }
};

/**
 * Validates that Medicare Part D effective date is not before Part A or Part B effective dates.
 * Ensures Part D coverage starts on or after the earliest Medicare part coverage.
 *
 * @param {Object} errors - The validation errors object to add errors to
 * @param {string} fieldData - The effective date to validate
 * @param {Object} formData - The complete form data for the current medicare item
 * @param {Object} formData.view:medicarePartAEffectiveDate - Part A date wrapper
 * @param {string} formData.view:medicarePartAEffectiveDate.medicarePartAEffectiveDate - Part A effective date
 * @param {Object} formData.view:medicarePartBEffectiveDate - Part B date wrapper
 * @param {string} formData.view:medicarePartBEffectiveDate.medicarePartBEffectiveDate - Part B effective date
 */
export const validatePartDEffectiveDate = (errors, fieldData, formData) => {
  if (!fieldData) return;

  const planType = (formData?.medicarePlanType ?? '').toLowerCase();
  const activeParts = planType === 'c' ? ['a', 'b'] : planType.split('');
  const dates = ['A', 'B']
    .filter(part => activeParts.includes(part.toLowerCase()))
    .map(part => ({
      part,
      date: formData?.[`view:medicarePart${part}EffectiveDate`]?.[
        `medicarePart${part}EffectiveDate`
      ],
    }))
    .filter(({ date }) => date && isValid(new Date(date)));

  if (!dates.length) return;

  const earliest = dates.reduce((min, current) =>
    isBefore(new Date(current.date), new Date(min.date)) ? current : min,
  );

  if (isBefore(new Date(fieldData), new Date(earliest.date))) {
    const formattedDate = formatDateLong(earliest.date);
    const msg = replaceStrValues(ERR_PART_D_EFFECT_DATE, [
      earliest.part,
      formattedDate,
    ]);
    errors.addError(msg);
  }
};
